import { useEffect } from "react";
import { v4 } from "uuid";
import { toast } from "sonner";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Confirmation } from "@/services/backend/htz/confirmations/types/confirmation";
import {
  PatchPositionRequest,
  updateHtzConfirmationCache,
  useHtzConfirmationAddPositionMutation,
  useHtzConfirmationPatchPositionMutation,
  useHtzConfirmationRemovePositionMutation,
  useHtzConfirmationShowQuery,
} from "@/services/backend/htz/confirmations/api/confirmation-api";
import {
  patchPosition,
  Position,
  positionPatchFromCatalog,
} from "@/services/backend/htz/position/types/position";
import { usePositionsTableDependencies } from "@/services/backend/htz/position/hooks/use-positions-table-dependencies";
import {
  PositionsTable,
  PositionsTableSkeleton,
} from "@/services/backend/htz/position/components/positions-table";
import { PositionRow } from "@/services/backend/htz/position/components/position-row";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

/**
 * ConfirmationPositionsTable edits the positions of the confirmation. Every change of a
 * position is saved on its own.
 */
export function ConfirmationPositionsTable({
  confirmationId,
}: {
  confirmationId: string;
}) {
  const { data: confirmation, error } = useHtzConfirmationShowQuery({
    id: confirmationId,
  });
  const {
    catalog,
    airHandlingUnits,
    isLoading,
    error: dependenciesError,
  } = usePositionsTableDependencies(
    confirmation?.companyId,
    confirmation?.customerId,
  );

  if (error || dependenciesError) {
    return <RTKQueryErrorAlert error={error ?? dependenciesError} />;
  }

  if (!confirmation || !catalog || isLoading) {
    return <PositionsTableSkeleton />;
  }

  return (
    <Positions
      confirmation={confirmation}
      catalog={catalog}
      airHandlingUnits={airHandlingUnits}
    />
  );
}

function Positions({
  confirmation,
  catalog,
  airHandlingUnits,
}: {
  confirmation: Confirmation;
  catalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
}) {
  const [add, { error, reset }] = useHtzConfirmationAddPositionMutation();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const disabled = isImmutable(confirmation);

  return (
    <PositionsTable
      positions={confirmation.positions}
      catalog={catalog}
      onAdd={(item) =>
        add({
          id: confirmation.id,
          positionId: v4(),
          ...positionPatchFromCatalog(item),
        })
      }
      disabled={disabled}
    >
      {confirmation.positions.map((position) => (
        <ConfirmationPositionRow
          key={position.id}
          confirmation={confirmation}
          position={position}
          airHandlingUnits={airHandlingUnits}
          disabled={disabled}
        />
      ))}
    </PositionsTable>
  );
}

function ConfirmationPositionRow({
  confirmation,
  position,
  airHandlingUnits,
  disabled,
}: {
  confirmation: Confirmation;
  position: Position;
  airHandlingUnits: AirHandlingUnit[];
  disabled: boolean;
}) {
  const request: PatchPositionRequest = {
    id: confirmation.id,
    positionId: position.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzConfirmationCache,
    useHtzConfirmationPatchPositionMutation(),
  );
  const [remove, { error, reset }] = useHtzConfirmationRemovePositionMutation();
  const { resetFields } = useErrValidationFailedContext();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  return (
    <PositionRow
      position={position}
      onPatch={(patch) =>
        set(
          { ...request, ...patch },
          {
            ...confirmation,
            positions: patchPosition(
              confirmation.positions,
              position.id,
              patch,
            ),
          },
        )
      }
      onRemove={() =>
        remove(request)
          .unwrap()
          .then(() => resetFields(joinFieldName("positions", position.id)))
          .catch(() => {
            // surfaced through the error state above
          })
      }
      airHandlingUnits={airHandlingUnits}
      disabled={disabled}
    />
  );
}
